/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useCallback } from 'react'
import { t } from '@sportninja/common/i18n'
import { getTeamOfficialTypesBySportId } from '@sportninja/common/selectors/types'
import { connect } from 'react-redux'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  toRosterOfficialRow,
  type RosterOfficialRow,
} from '@sportninja/common/features/competition/operations/utils/rosterRows'
import { sortByLastName } from '@sportninja/common/features/team/roster/utils/sortByLastName'
import { SendInvitationTrigger } from 'src/components/SendInvitationModal'
import OptionButtonDelete from 'src/components/List/OptionButtons/Delete'
import MoreOptions from 'src/components/List/OptionButtons/More'
import TeamOfficialModal from 'src/components/TeamOfficialModal'
import RosterGrid from 'src/features/competition/operations/components/roster/RosterGrid'
import { useRosterOfficialsColumns } from 'src/features/competition/operations/grid/useRosterOfficialsColumns'
import ColumnChooser from 'src/components/ts-components/ColumnChooser'
import { useColumnVisibility } from 'src/hooks/useColumnVisibility'
import {
  applyHiddenColumns,
  chooserColumnsOf,
  ROSTER_LOCKED_COLUMN_IDS,
} from 'src/features/competition/operations/utils/rosterColumnVisibility'
import { useRosterContext } from '../context/RosterContext'
import { useUIContext } from '../context/UIContext'
import { useScrollToRow } from '../hooks/useScrollToRow'
import SectionTitle from './SectionTitle'

/** No row ever has a save in flight here — cells are read-only. */
const NO_PENDING_IDS = new Set<string>()

interface TeamOfficialSectionProps {
  teamId: string
  permission: Record<string, any>
  teamOfficialTypes: any[]
  sportId?: string
}

const TeamOfficialSection = ({
  teamId,
  permission,
  teamOfficialTypes,
  sportId,
}: TeamOfficialSectionProps) => {
  const { teamOfficials, selectedRosterId, refresh } = useRosterContext()
  const {
    highlightedRows,
    addHighlightedRow,
    selectedOfficial,
    setSelectedOfficial,
    isOfficialModalOpen,
    setOfficialModalOpen,
  } = useUIContext()
  const scrollToRow = useScrollToRow()

  const handleEditClick = useCallback(
    (item: any) => {
      setSelectedOfficial(item)
      setOfficialModalOpen(true)
    },
    [setSelectedOfficial, setOfficialModalOpen]
  )

  const handleModalClose = useCallback(() => {
    setOfficialModalOpen(false)
    setSelectedOfficial(null)
  }, [setOfficialModalOpen, setSelectedOfficial])

  const handleModalSuccess = useCallback(async () => {
    await refresh()
    if (selectedOfficial?.id) {
      addHighlightedRow(selectedOfficial.id, 'success')
      scrollToRow(selectedOfficial.id)
    }
  }, [refresh, selectedOfficial, addHighlightedRow, scrollToRow])

  const getOptions = useCallback(
    (item: any) => {
      const slug = `/teams/${teamId}/rosters/${selectedRosterId}/team-officials/${item.id}`

      const opts = [
        <MoreOptions
          key='more'
          options={[
            // @ts-ignore
            {
              key: 'edit',
              title: capitalizeWords('Edit Profile'),
              iconName: 'pencil',
              onClick: () => handleEditClick(item),
            },
            // Renders as Resend Invitation or Send Invitation depending on the
            // row's invite state. Hidden on exactly the rows
            // SendInvitationTrigger renders nothing for (invited AND accepted).
            // @ts-ignore
            !(item.is_invited && item.is_accepted) && {
              key: 'invite',
              Component: SendInvitationTrigger,
              componentProps: {
                asMenuItem: true,
                invite: {
                  is_invited: item.is_invited,
                  is_accepted: item.is_accepted,
                  invitation_id: item.invitation_id,
                },
                user: {
                  id: item.id,
                  name_first: item.name_first,
                  name_last: item.name_last,
                  team_official_type_id: item.team_official_type,
                  email: item?.email,
                },
                slug,
                onComplete: refresh,
              },
            },
            // @ts-ignore
            {
              key: 'remove',
              Component: OptionButtonDelete,
              componentProps: {
                buttonText: 'remove',
                slug,
                iconName: 'user-minus',
                title: `Remove ${item.name_full}`,
                type: t('common:teamOfficial', {}),
                itemId: item.id,
                onComplete: refresh,
              },
              title: 'remove',
              iconName: 'user-minus',
            },
          ]}
        />,
      ]

      return opts.filter((o) => o !== undefined)
    },
    [teamId, selectedRosterId, refresh, handleEditClick]
  )

  /** The raw context row for an id — the menu's flows need the full object. */
  const rawOfficial = (id: string) =>
    teamOfficials.find((official: { id: string }) => official.id === id)

  // Same columns, order and titles as the Operations roster grid. Cells stay
  // read-only (no `onEditField`) and the Team screen keeps its own menu.
  const columns = useRosterOfficialsColumns({
    canEdit: Boolean(permission?.update || permission?.admin),
    officialTypes: teamOfficialTypes ?? [],
    renderActions: (row: RosterOfficialRow) => {
      const official = rawOfficial(row.id)
      return official ? (
        <div css={actionsStyle}>{getOptions(official)}</div>
      ) : null
    },
  })

  // Same chooser as the Operations roster. No storageKey: the choice lasts for
  // this visit only and resets on refresh, as it does there.
  const visibility = useColumnVisibility({
    columns: chooserColumnsOf(columns, ROSTER_LOCKED_COLUMN_IDS),
  })
  const shownColumns = applyHiddenColumns(columns, visibility.isHidden)

  // Last name first by default, as the Team screen has always listed them.
  const officialRows: RosterOfficialRow[] =
    sortByLastName(teamOfficials).map(toRosterOfficialRow)
  const highlightedIds = new Set<string>(highlightedRows.keys())

  if (officialRows.length === 0) {
    return (
      <div>
        <SectionTitle>Team Officials</SectionTitle>
        <p
          css={css`
            color: ${colors.NEUTRAL_50};
            font-size: 14px;
          `}
        >
          No team officials have been added to this roster yet.
        </p>
      </div>
    )
  }

  return (
    <div
      css={css`
        width: 100%;
        min-width: 0;
      `}
    >
      <SectionTitle
        action={
          <ColumnChooser
            label={capitalizeWords(t('common:gameGridColumnsButton', {}))}
            title={capitalizeWords(t('common:gameGridColumnsTitle', {}))}
            columns={visibility.columns}
            onToggle={visibility.setColumnVisible}
            onReset={visibility.resetColumns}
            data-testid='team-roster-officials-column-chooser'
          />
        }
      >
        Team Officials
      </SectionTitle>
      <RosterGrid
        rows={officialRows}
        columns={shownColumns}
        pendingIds={NO_PENDING_IDS}
        highlightedIds={highlightedIds}
        loading={false}
        testId='team-roster-officials'
      />
      <TeamOfficialModal
        isOpen={isOfficialModalOpen}
        onClose={handleModalClose}
        initialData={selectedOfficial}
        teamId={teamId}
        rosterId={selectedRosterId}
        onSuccess={handleModalSuccess}
        sportId={sportId}
      />
    </div>
  )
}

/* Centres the ⋯ trigger in the pinned actions column. */
const actionsStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

const mapStateToProps = (state: any, { sportId }: any) => {
  return {
    teamOfficialTypes: getTeamOfficialTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(TeamOfficialSection)
